import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { api } from "@/lib/api-client"
import type {
  Workflow,
  WorkflowDetail,
  WorkflowExecution,
  WorkflowExecutionDetail,
  PaginatedResponse,
} from "@/lib/types"

interface WorkflowFilters {
  is_active?: boolean
  trigger_type?: string
  search?: string
}

export function useWorkflows(filters?: WorkflowFilters) {
  const params = new URLSearchParams()
  if (filters?.is_active !== undefined) params.set("is_active", String(filters.is_active))
  if (filters?.trigger_type) params.set("trigger_type", filters.trigger_type)
  if (filters?.search) params.set("search", filters.search)
  const qs = params.toString()

  return useQuery({
    queryKey: ["workflows", filters],
    queryFn: () =>
      api.get<PaginatedResponse<Workflow>>(`/api/v1/workflows${qs ? `?${qs}` : ""}`),
  })
}

export function useWorkflow(id: string) {
  return useQuery({
    queryKey: ["workflows", id],
    queryFn: () => api.get<{ data: WorkflowDetail }>(`/api/v1/workflows/${id}`),
    enabled: !!id,
  })
}

export function useCreateWorkflow() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      api.post<{ data: WorkflowDetail }>("/api/v1/workflows", data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["workflows"] })
    },
  })
}

export function useUpdateWorkflow() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      api.patch<{ data: WorkflowDetail }>(`/api/v1/workflows/${id}`, data),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: ["workflows"] })
      qc.invalidateQueries({ queryKey: ["workflows", id] })
    },
  })
}

export function useDeleteWorkflow() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/v1/workflows/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["workflows"] })
    },
  })
}

export function useActivateWorkflow() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      api.post<{ data: Workflow }>(`/api/v1/workflows/${id}/activate`),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: ["workflows"] })
      qc.invalidateQueries({ queryKey: ["workflows", id] })
    },
  })
}

export function useDeactivateWorkflow() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      api.post<{ data: Workflow }>(`/api/v1/workflows/${id}/deactivate`),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: ["workflows"] })
      qc.invalidateQueries({ queryKey: ["workflows", id] })
    },
  })
}

export function useWorkflowExecutions(workflowId: string) {
  return useQuery({
    queryKey: ["workflows", workflowId, "executions"],
    queryFn: () =>
      api.get<PaginatedResponse<WorkflowExecution>>(
        `/api/v1/workflows/${workflowId}/executions`
      ),
    enabled: !!workflowId,
  })
}

export function useWorkflowExecutionDetail(workflowId: string, executionId: string) {
  return useQuery({
    queryKey: ["workflows", workflowId, "executions", executionId],
    queryFn: () =>
      api.get<{ data: WorkflowExecutionDetail }>(
        `/api/v1/workflows/${workflowId}/executions/${executionId}`
      ),
    enabled: !!workflowId && !!executionId,
  })
}
